/**
 * HeaderBrandingCard - Company Settings section for custom header logos
 *
 * Manages wordmark + icon uploads with light/dark variants,
 * display mode (replace vs collab), and single-image toggles.
 */

import { Sun, Moon } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { toast } from 'react-toastify';
import { ImageUploader } from '@/components/upload/ImageUploader';
import { CollabLogo } from '@/components/layout/CollabLogo';
import { uploadCompanyLogoVariant, deleteCompanyLogoVariant } from '@/lib/adminApi';
import type { LogoDisplayMode } from '@/types';

interface ThemeSettings {
  logo_light_url: string | null;
  logo_dark_url: string | null;
  icon_light_url: string | null;
  icon_dark_url: string | null;
  use_custom_logo: boolean;
  logo_display_mode: LogoDisplayMode;
  use_single_logo: boolean;
  use_single_icon: boolean;
}

interface HeaderBrandingCardProps {
  themeSettings: ThemeSettings;
  setThemeSettings: React.Dispatch<React.SetStateAction<ThemeSettings>>;
  saveThemeSettings: (updates: Partial<ThemeSettings>) => Promise<void>;
  companySlug: string | undefined;
  companyName: string;
  refresh: () => Promise<void>;
}

const LOGO_ACCEPT = {
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/webp': ['.webp'],
  'image/svg+xml': ['.svg'],
};

function extractUrl(logo: { svg_url?: string | null; medium_webp_url?: string | null; medium_png_url?: string | null; original_url: string }) {
  return logo.svg_url || logo.medium_webp_url || logo.medium_png_url || logo.original_url;
}

export function HeaderBrandingCard({
  themeSettings,
  setThemeSettings,
  saveThemeSettings,
  companySlug,
  companyName,
  refresh,
}: HeaderBrandingCardProps) {
  return (
    <div className="bg-card border border-border rounded-xl p-4 sm:p-6">
      <h3 className="text-lg font-semibold text-white mb-3 sm:mb-4">Header Branding</h3>
      <p className="text-sm text-muted-foreground mb-3 sm:mb-4">
        Upload custom logos to display in the navigation header instead of the default Hollywood Trucks logo.
      </p>

      {/* Toggle for custom logo */}
      <div className="flex items-center justify-between py-3 border-b border-border mb-4">
        <div>
          <p className="text-sm font-medium text-white">Use Custom Logo</p>
          <p className="text-xs text-muted-foreground">Display your company logo in the header</p>
        </div>
        <Switch
          checked={themeSettings.use_custom_logo}
          onCheckedChange={(checked) => saveThemeSettings({ use_custom_logo: checked })}
        />
      </div>

      {/* Logo Display Mode Selector */}
      {themeSettings.use_custom_logo && (
        <div className="mb-4">
          <p className="text-sm font-medium text-white mb-2">Display Mode</p>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => saveThemeSettings({ logo_display_mode: 'replace' })}
              className={`flex flex-col items-center gap-1.5 px-3 py-3 rounded-lg border text-center transition-colors ${
                themeSettings.logo_display_mode === 'replace'
                  ? 'border-[#8FB560] bg-[#8FB560]/10'
                  : 'border-border hover:border-muted-foreground'
              }`}
            >
              <div className="flex items-center justify-center h-6">
                <div className="px-2 py-0.5 bg-muted rounded text-[10px] font-medium text-muted-foreground">LOGO</div>
              </div>
              <span className={`text-xs font-medium ${themeSettings.logo_display_mode === 'replace' ? 'text-[#8FB560]' : 'text-muted-foreground'}`}>
                Replace
              </span>
              <span className="text-[10px] text-muted-foreground leading-tight">
                Your logo only
              </span>
            </button>
            <button
              type="button"
              onClick={() => saveThemeSettings({ logo_display_mode: 'collab' })}
              className={`flex flex-col items-center gap-1.5 px-3 py-3 rounded-lg border text-center transition-colors ${
                themeSettings.logo_display_mode === 'collab'
                  ? 'border-[#8FB560] bg-[#8FB560]/10'
                  : 'border-border hover:border-muted-foreground'
              }`}
            >
              <div className="flex items-center justify-center h-6 gap-1">
                <span className="text-[10px] font-bold text-[#7ED957]">HT</span>
                <span className="text-[9px] text-muted-foreground">x</span>
                <div className="px-1.5 py-0.5 bg-muted rounded text-[10px] font-medium text-muted-foreground">LOGO</div>
              </div>
              <span className={`text-xs font-medium ${themeSettings.logo_display_mode === 'collab' ? 'text-[#8FB560]' : 'text-muted-foreground'}`}>
                Collaboration
              </span>
              <span className="text-[10px] text-muted-foreground leading-tight">
                HT x Your logo
              </span>
            </button>
          </div>
        </div>
      )}

      {/* ─── Wordmark Section ─── */}
      <div className="mb-5">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-white">Wordmark</span>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <span className="text-[10px] text-muted-foreground">Same for both</span>
            <Switch
              checked={themeSettings.use_single_logo}
              onCheckedChange={(checked) => saveThemeSettings({ use_single_logo: checked })}
              className="scale-75 origin-right"
            />
          </label>
        </div>

        {themeSettings.use_single_logo ? (
          <ImageUploader
            currentImage={themeSettings.logo_light_url || themeSettings.logo_dark_url}
            onUpload={async (file) => {
              if (!companySlug) return;
              const result = await uploadCompanyLogoVariant(companySlug, file, 'light');
              const logoUrl = extractUrl(result.logo);
              setThemeSettings(prev => ({ ...prev, logo_light_url: logoUrl, logo_dark_url: logoUrl }));
              await saveThemeSettings({ logo_light_url: logoUrl, logo_dark_url: logoUrl });
              toast.success('Wordmark uploaded');
            }}
            onRemove={async () => {
              if (!companySlug) return;
              await deleteCompanyLogoVariant(companySlug, 'light');
              await deleteCompanyLogoVariant(companySlug, 'dark');
              setThemeSettings(prev => ({ ...prev, logo_light_url: null, logo_dark_url: null }));
              toast.success('Wordmark removed');
            }}
            accept={LOGO_ACCEPT}
            shape="square"
            size="wide"
            imageType="logo"
            helpText="Used for both light and dark modes"
          />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <div className="flex items-center gap-1.5 mb-1.5">
                <Sun className="w-3.5 h-3.5 text-amber-400" />
                <span className="text-xs text-muted-foreground">Light</span>
              </div>
              <ImageUploader
                currentImage={themeSettings.logo_light_url}
                onUpload={async (file) => {
                  if (!companySlug) return;
                  const result = await uploadCompanyLogoVariant(companySlug, file, 'light');
                  const logoUrl = extractUrl(result.logo);
                  setThemeSettings(prev => ({ ...prev, logo_light_url: logoUrl }));
                  await refresh();
                  toast.success('Light mode wordmark uploaded');
                }}
                onRemove={async () => {
                  if (!companySlug) return;
                  await deleteCompanyLogoVariant(companySlug, 'light');
                  setThemeSettings(prev => ({ ...prev, logo_light_url: null }));
                  toast.success('Light mode wordmark removed');
                }}
                accept={LOGO_ACCEPT}
                shape="square"
                size="wide"
                imageType="logo"
                helpText="Light backgrounds"
              />
            </div>
            <div>
              <div className="flex items-center gap-1.5 mb-1.5">
                <Moon className="w-3.5 h-3.5 text-blue-400" />
                <span className="text-xs text-muted-foreground">Dark</span>
              </div>
              <ImageUploader
                currentImage={themeSettings.logo_dark_url}
                onUpload={async (file) => {
                  if (!companySlug) return;
                  const result = await uploadCompanyLogoVariant(companySlug, file, 'dark');
                  const logoUrl = extractUrl(result.logo);
                  setThemeSettings(prev => ({ ...prev, logo_dark_url: logoUrl }));
                  await refresh();
                  toast.success('Dark mode wordmark uploaded');
                }}
                onRemove={async () => {
                  if (!companySlug) return;
                  await deleteCompanyLogoVariant(companySlug, 'dark');
                  setThemeSettings(prev => ({ ...prev, logo_dark_url: null }));
                  toast.success('Dark mode wordmark removed');
                }}
                accept={LOGO_ACCEPT}
                shape="square"
                size="wide"
                imageType="logo"
                helpText="Dark backgrounds"
              />
            </div>
          </div>
        )}
      </div>

      {/* ─── Icon Section ─── */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div>
            <span className="text-sm font-medium text-white">Icon</span>
            <p className="text-[10px] text-muted-foreground">Compact mark for mobile header</p>
          </div>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <span className="text-[10px] text-muted-foreground">Same for both</span>
            <Switch
              checked={themeSettings.use_single_icon}
              onCheckedChange={(checked) => saveThemeSettings({ use_single_icon: checked })}
              className="scale-75 origin-right"
            />
          </label>
        </div>

        {themeSettings.use_single_icon ? (
          <ImageUploader
            currentImage={themeSettings.icon_light_url || themeSettings.icon_dark_url}
            onUpload={async (file) => {
              if (!companySlug) return;
              const result = await uploadCompanyLogoVariant(companySlug, file, 'icon_light');
              const logoUrl = extractUrl(result.logo);
              setThemeSettings(prev => ({ ...prev, icon_light_url: logoUrl, icon_dark_url: logoUrl }));
              await saveThemeSettings({ icon_light_url: logoUrl, icon_dark_url: logoUrl });
              toast.success('Icon uploaded');
            }}
            onRemove={async () => {
              if (!companySlug) return;
              await deleteCompanyLogoVariant(companySlug, 'icon_light');
              await deleteCompanyLogoVariant(companySlug, 'icon_dark');
              setThemeSettings(prev => ({ ...prev, icon_light_url: null, icon_dark_url: null }));
              toast.success('Icon removed');
            }}
            accept={LOGO_ACCEPT}
            shape="square"
            size="md"
            imageType="logo"
            helpText="Used for both light and dark modes"
          />
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="flex items-center gap-1.5 mb-1.5">
                <Sun className="w-3.5 h-3.5 text-amber-400" />
                <span className="text-xs text-muted-foreground">Light</span>
              </div>
              <ImageUploader
                currentImage={themeSettings.icon_light_url}
                onUpload={async (file) => {
                  if (!companySlug) return;
                  const result = await uploadCompanyLogoVariant(companySlug, file, 'icon_light');
                  const logoUrl = extractUrl(result.logo);
                  setThemeSettings(prev => ({ ...prev, icon_light_url: logoUrl }));
                  await refresh();
                  toast.success('Light mode icon uploaded');
                }}
                onRemove={async () => {
                  if (!companySlug) return;
                  await deleteCompanyLogoVariant(companySlug, 'icon_light');
                  setThemeSettings(prev => ({ ...prev, icon_light_url: null }));
                  toast.success('Light mode icon removed');
                }}
                accept={LOGO_ACCEPT}
                shape="square"
                size="sm"
                imageType="logo"
                helpText="Light"
              />
            </div>
            <div>
              <div className="flex items-center gap-1.5 mb-1.5">
                <Moon className="w-3.5 h-3.5 text-blue-400" />
                <span className="text-xs text-muted-foreground">Dark</span>
              </div>
              <ImageUploader
                currentImage={themeSettings.icon_dark_url}
                onUpload={async (file) => {
                  if (!companySlug) return;
                  const result = await uploadCompanyLogoVariant(companySlug, file, 'icon_dark');
                  const logoUrl = extractUrl(result.logo);
                  setThemeSettings(prev => ({ ...prev, icon_dark_url: logoUrl }));
                  await refresh();
                  toast.success('Dark mode icon uploaded');
                }}
                onRemove={async () => {
                  if (!companySlug) return;
                  await deleteCompanyLogoVariant(companySlug, 'icon_dark');
                  setThemeSettings(prev => ({ ...prev, icon_dark_url: null }));
                  toast.success('Dark mode icon removed');
                }}
                accept={LOGO_ACCEPT}
                shape="square"
                size="sm"
                imageType="logo"
                helpText="Dark"
              />
            </div>
          </div>
        )}
      </div>

      {/* Preview */}
      {(themeSettings.logo_light_url || themeSettings.logo_dark_url || themeSettings.icon_light_url || themeSettings.icon_dark_url) && (
        <div className="mt-4 pt-4 sm:mt-6 sm:pt-6 border-t border-border">
          <p className="text-sm font-medium text-white mb-3">Preview</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* Desktop Preview */}
            <div className="rounded-lg overflow-hidden border border-border">
              <div className="bg-zinc-900 px-3 py-1.5 flex items-center gap-2">
                <span className="text-[10px] text-zinc-500 uppercase tracking-wide">Desktop</span>
              </div>
              <div className="bg-zinc-800 px-3 py-3 flex items-center justify-center min-h-[56px]">
                {themeSettings.logo_display_mode === 'collab' && (themeSettings.logo_dark_url || themeSettings.logo_light_url) ? (
                  <CollabLogo
                    partnerLogoUrl={themeSettings.logo_dark_url || themeSettings.logo_light_url || ''}
                    partnerName={companyName}
                    isDarkMode={true}
                    size="sm"
                  />
                ) : (themeSettings.logo_dark_url || themeSettings.logo_light_url) ? (
                  <img
                    src={themeSettings.logo_dark_url || themeSettings.logo_light_url || ''}
                    alt="Desktop logo preview"
                    className="h-8 w-auto max-w-full object-contain"
                  />
                ) : (
                  <span className="text-xs text-zinc-500">No wordmark</span>
                )}
              </div>
            </div>
            {/* Mobile Preview */}
            <div className="rounded-lg overflow-hidden border border-border">
              <div className="bg-zinc-900 px-3 py-1.5 flex items-center gap-2">
                <span className="text-[10px] text-zinc-500 uppercase tracking-wide">Mobile</span>
              </div>
              <div className="bg-zinc-800 px-3 py-3 flex items-center justify-center min-h-[56px]">
                {themeSettings.logo_display_mode === 'collab' && (themeSettings.icon_dark_url || themeSettings.icon_light_url) ? (
                  <CollabLogo
                    partnerLogoUrl={themeSettings.icon_dark_url || themeSettings.icon_light_url || ''}
                    partnerName={companyName}
                    isDarkMode={true}
                    size="sm"
                  />
                ) : (themeSettings.icon_dark_url || themeSettings.icon_light_url) ? (
                  <img
                    src={themeSettings.icon_dark_url || themeSettings.icon_light_url || ''}
                    alt="Mobile icon preview"
                    className="h-7 w-auto max-w-[48px] object-contain"
                  />
                ) : (
                  <span className="text-2xl font-bold text-[#7ED957]">HT</span>
                )}
              </div>
            </div>
          </div>
          <p className="text-[10px] text-muted-foreground mt-2">
            {themeSettings.logo_display_mode === 'collab'
              ? 'Collaboration mode shows the HT logo alongside your company logo.'
              : 'Desktop uses the wordmark, mobile uses the icon (falls back to HT).'}
          </p>
        </div>
      )}
    </div>
  );
}
